// What the Network map draws, as pure functions of the API data, so tests can assert the mapping
// without rendering. Every `now` is a parameter: tests pin it.
import type { Device, DeviceStatus, Flow, Network, NetworkMatrix, ThroughputResult } from '../../api/types';
import { formatMbit, toMbit } from '../../lib/format';
import { slash24 } from '../../lib/networks';
import { presence, type Presence } from '../../lib/status';
import { formatDuration } from '../traffic/model';

/** Legacy devices (network null) share one lane; the colon keeps it apart from every network id. */
export const LEGACY_LANE = ':legacy';
/** The relay's own /24, where the pre-networks devices were numbered. */
export const LEGACY_CIDR = '10.88.0.0/24';

export interface Lane {
  id: string;
  name: string;
  cidr: string;
  isolated: boolean;
  /** The "legacy" lane, or a network id GET /networks no longer lists. */
  known: boolean;
  /** By overlay address. */
  devices: Device[];
}

const ipInt = (ip: string) => ip.split('.').reduce((n, o) => n * 256 + Number(o), 0);
const cidrInt = (cidr: string) => ipInt(cidr.split('/')[0]!);
const byIp = (a: Device, b: Device) => ipInt(a.overlayIp) - ipInt(b.overlayIp);

/**
 * One lane per network, by CIDR, each holding its devices by address. Devices naming a network
 * the list does not have still get a lane (cidr from their address), and legacy devices get the
 * "legacy" lane at the end, only when there are any.
 */
export function buildLanes(networks: readonly Network[], devices: readonly Device[]): Lane[] {
  const lanes = new Map<string, Lane>();
  for (const n of [...networks].sort((a, b) => cidrInt(a.cidr) - cidrInt(b.cidr))) {
    lanes.set(n.id, { id: n.id, name: n.name, cidr: n.cidr, isolated: n.isolated, known: true, devices: [] });
  }
  const orphans = new Map<string, Lane>();
  const legacy: Device[] = [];
  for (const d of devices) {
    if (!d.network) {
      legacy.push(d);
      continue;
    }
    let lane = lanes.get(d.network) ?? orphans.get(d.network);
    if (!lane) {
      lane = { id: d.network, name: d.network, cidr: slash24(d.overlayIp), isolated: false, known: false, devices: [] };
      orphans.set(d.network, lane);
    }
    lane.devices.push(d);
  }
  const out = [...lanes.values(), ...[...orphans.values()].sort((a, b) => cidrInt(a.cidr) - cidrInt(b.cidr))];
  if (legacy.length) out.push({ id: LEGACY_LANE, name: 'legacy', cidr: LEGACY_CIDR, isolated: false, known: false, devices: legacy });
  for (const l of out) l.devices.sort(byIp);
  return out;
}

/** The map's dot: the shared presence, plus "idle" for a device that handshook recently but is not online now. */
export type MapState = Presence | 'idle';

/** Handshook within this long but not within the online window: connected a moment ago, quiet now. */
export const IDLE_WINDOW_MS = 15 * 60_000;

export function mapState(
  deviceId: string,
  status: DeviceStatus | null | undefined,
  live: ReadonlySet<string>,
  now: number,
): MapState {
  const p = presence(deviceId, status, live, now);
  if (p !== 'offline') return p;
  const t = status?.lastHandshakeAt ? Date.parse(status.lastHandshakeAt) : NaN;
  return Number.isFinite(t) && now - t <= IDLE_WINDOW_MS ? 'idle' : 'offline';
}

/** The card's "seen": "now" while live, else the handshake age as "2s", "11m", "3h", "2d"; "never". */
export function seenText(state: MapState, status: DeviceStatus | null | undefined, now: number): string {
  if (state === 'live') return 'now';
  const t = status?.lastHandshakeAt ? Date.parse(status.lastHandshakeAt) : NaN;
  if (!Number.isFinite(t)) return 'never';
  const s = Math.max(0, Math.floor((now - t) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3_600) return `${Math.floor(s / 60)}m`;
  if (s < 86_400) return `${Math.floor(s / 3_600)}h`;
  return `${Math.floor(s / 86_400)}d`;
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/**
 * A lane's policy posture toward the other networks, from GET /networks/matrix: "open to lab ·
 * denies guest", "2 rules to core", "denied to everything". Null when the matrix does not know
 * the lane (legacy, or not loaded).
 */
export function postureText(laneId: string, matrix: NetworkMatrix | null | undefined): string | null {
  if (!matrix || !matrix.networks.includes(laneId)) return null;
  const out = matrix.networks
    .filter((d) => d !== laneId)
    .map((d) => matrix.cells.find((c) => c.source === laneId && c.destination === d))
    .filter((c) => c !== undefined);
  if (out.length === 0) return 'no other networks';
  if (out.every((c) => c.reach === 'deny')) return 'denied to everything';
  if (out.every((c) => c.reach === 'allow')) return 'open to everything';
  const open = out.filter((c) => c.reach === 'allow').map((c) => c.destination);
  const parts: string[] = [];
  if (open.length) parts.push(`open to ${open.join(', ')}`);
  for (const c of out) if (c.reach === 'partial') parts.push(`${plural(c.ruleIds.length, 'rule')} to ${c.destination}`);
  const denied = out.filter((c) => c.reach === 'deny').map((c) => c.destination);
  if (denied.length) parts.push(`denies ${denied.join(', ')}`);
  return parts.join(' · ');
}

/** A live device-to-device flow, placed on the map. */
export interface LiveSession {
  id: string;
  srcId: string;
  dstId: string;
  srcLane: string;
  dstLane: string;
  /** "wojciech-laptop → relay → wdembins-deskto". */
  path: string;
  /** "3389/tcp", or the bare protocol when there is no port. */
  service: string;
  /** "4.6 Mbit/s · 3389/tcp · 14m 02s". */
  meta: string;
  /** "both on core — still via the relay" when both ends share a lane; else null. */
  sameNote: string | null;
}

/**
 * Live allowed flows whose ends are both devices on the map. Every one of them crosses the relay,
 * even inside one network, which is the fact the map draws. The rate is the flow's average.
 */
export function liveSessions(flows: readonly Flow[] | null | undefined, lanes: readonly Lane[]): LiveSession[] {
  const laneOf = new Map<string, Lane>();
  for (const l of lanes) for (const d of l.devices) laneOf.set(d.id, l);
  const out: LiveSession[] = [];
  for (const f of flows ?? []) {
    if (!f.live || f.result !== 'allowed' || !f.src.deviceId || !f.dst.deviceId) continue;
    const a = laneOf.get(f.src.deviceId);
    const b = laneOf.get(f.dst.deviceId);
    if (!a || !b) continue;
    const name = (e: Flow['src']) => e.name ?? e.ip;
    const rate = f.bytes !== null && f.durationSeconds > 0 ? formatMbit(toMbit(f.bytes, f.durationSeconds)) : '–';
    const service = f.port !== null ? `${f.port}/${f.proto}` : f.proto;
    out.push({
      id: f.id,
      srcId: f.src.deviceId,
      dstId: f.dst.deviceId,
      srcLane: a.id,
      dstLane: b.id,
      path: `${name(f.src)} → relay → ${name(f.dst)}`,
      service,
      meta: [rate, service, formatDuration(f.durationSeconds)].join(' · '),
      sameNote: a.id === b.id ? `both on ${a.name} — still via the relay` : null,
    });
  }
  return out;
}

/**
 * The relay card's rate: the newest complete point of GET /metrics/throughput?range=1h&by=total
 * (the last one is the bucket still filling), rx + tx. Null when there is no such point.
 */
export function relayMbit(t: ThroughputResult | null | undefined, now: number): number | null {
  const s = t?.series[0];
  if (!t || !s || t.points === 0) return null;
  const from = Date.parse(t.from);
  const filled = Number.isFinite(from) ? Math.floor((now - from) / (t.stepSeconds * 1000)) - 1 : t.points - 1;
  const i = Math.min(t.points - 1, filled);
  if (i < 0) return null;
  return toMbit((s.rx[i] ?? 0) + (s.tx[i] ?? 0), t.stepSeconds);
}

export type DeviceKind = 'router' | 'storage' | 'laptop' | 'desktop' | 'tv' | 'board' | 'device';

/** The card's icon, guessed from tags (the API has no device type). */
export function deviceKind(d: Pick<Device, 'tags' | 'name'>): DeviceKind {
  const has = (...t: string[]) => t.some((x) => d.tags.includes(x));
  if (has('router', 'site')) return 'router';
  if (has('storage', 'nas', 'server')) return 'storage';
  if (has('laptop')) return 'laptop';
  if (has('workstation', 'desktop', 'rdp')) return 'desktop';
  if (has('tv', 'guest', 'media')) return 'tv';
  if (has('sensor', 'pi', 'iot')) return 'board';
  return 'device';
}
